import React, { createContext, useState, ReactNode, useEffect, useRef } from 'react';

interface ThemeContextProps {
  theme: string;
  setTheme: (theme: string) => void;
  setThemes: (themes: string[]) => void;
}

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeContext = createContext<ThemeContextProps>({
  theme: 'light',
  setTheme: () => {},
  setThemes: ([]) => {},
});

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const firstTheme = 'light';
  const [theme, setTheme] = useState<string>(firstTheme);
  const [themes, setThemes] = useState<string[] | null>(null);
  const previousTheme = useRef<string>(firstTheme);
  const secondaryStyleElements = useRef<HTMLStyleElement[]>([]);
  const primaryStyleElements = useRef<HTMLStyleElement[]>([]);

  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute(
        'content',
        theme === 'light'
          ? '#ffffff'
          : theme === 'dark'
            ? '#000000'
            : theme === 'thief'
              ? '#FFB500'
              : '#000000',
      );

    secondaryStyleElements.current.forEach((style) => style.remove());
    while (secondaryStyleElements.current.length !== 0)
      secondaryStyleElements.current.pop();
    if (previousTheme.current !== theme) {
      primaryStyleElements.current.forEach((style) => style.remove());
      while (primaryStyleElements.current.length !== 0)
        primaryStyleElements.current.pop();
    }

    if (themes != null && (themes.length !== 1 || themes[0] !== '')) {
      try {
        if (!document.getElementById(`common-styles`)) {
          fetch(`/styles/common.css`).then((value) => {
            value.text().then((value) => {
              const styleElement = document.createElement('style');
              styleElement.id = 'common-styles';
              styleElement.textContent = value;
              document.head.appendChild(styleElement);
            });
          });
        }
        if (!document.getElementById(`color-styles`)) {
          fetch(`/styles/${theme}/colors.css`).then((value) => {
            value.text().then((value) => {
              const styleElement = document.createElement('style');
              styleElement.id = `color-styles`;
              styleElement.textContent = value;
              document.head.appendChild(styleElement);
              primaryStyleElements.current.push(styleElement);
            });
          });
        }
        if (!document.getElementById(`fonts-styles`)) {
          fetch(`/styles/fonts.css`).then((value) => {
            value.text().then((value) => {
              const styleElement = document.createElement('style');
              styleElement.id = `fonts-styles`;
              styleElement.textContent = value;
              document.head.appendChild(styleElement);
              primaryStyleElements.current.push(styleElement);
            });
          });
        }
        if (!document.getElementById(`top-bar-styles-${theme}`)) {
          fetch(`/styles/${theme}/topbar.css`).then((value) => {
            value.text().then((value) => {
              const styleElement = document.createElement('style');
              styleElement.id = `top-bar-styles`;
              styleElement.textContent = value;
              document.head.appendChild(styleElement);
              primaryStyleElements.current.push(styleElement);
            });
          });
        }
        if (!document.getElementById(`general-styles`)) {
          fetch(`/styles/${theme}/general.css`).then((value) => {
            value.text().then((value) => {
              const styleElement = document.createElement('style');
              styleElement.id = `general-styles`;
              styleElement.textContent = value;
              document.head.appendChild(styleElement);
              primaryStyleElements.current.push(styleElement);
            });
          });
        }
      } catch (error) {
        console.error('Error loading common and font CSS:', error);
      }
      themes.forEach((element) => {
        try {
          fetch(`/styles/${theme}/${element}.css`).then((value) => {
            value.text().then((value) => {
              const styleElement = document.createElement('style');
              styleElement.textContent = value;
              document.head.appendChild(styleElement);
              secondaryStyleElements.current.push(styleElement);
            });
          });
        } catch (error) {
          console.error('Error loading CSS for theme:', error);
        }
      });
    }
    previousTheme.current = theme;
  }, [theme, themes, primaryStyleElements]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, setThemes }}>
      {children}
    </ThemeContext.Provider>
  );
};
